import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from "uuid";
import {
  Add,
} from "@mui/icons-material";
import { Tooltip, Fab } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  Character,
  Creature,
  BasicCreatureInstance,
  CustomParticipant,
  Encounter,
  // EncounterLog,
  EncounterParticipant,
} from "@dndModels";
import { DisablableOption } from "@models";
import { CR_OPTIONS } from "@dndModels/Constants";

import ShieldWhite from "../../../../assets/img/Shield_white.png";
import ShieldBlack from "../../../../assets/img/Shield_black.png";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import ColorButtonBox from "@components/input/buttons/ColorButtonBox";
import EditableLabel from "@components/input/strings/EditableLabel";
import HPInput from "@dndComponents/other/HPInput";
import CharacterResourceBoxesNew from "@dndComponents/character/misc/CharacterResourceBoxes";
import LoadingBox from "@components/input/LoadingBox";
import ConditionSideBar from "@dndComponents/other/ConditionSideBar";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import ExpandButton from "@components/input/buttons/ExpandButton";
import ButtonBox from "@components/input/buttons/ButtonBox";
import CreatureInstanceSavingThrows from "@dndComponents/creature/CreatureInstanceSavingThrows";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import ClearButton from "@components/input/buttons/ClearButton";
import StringBox from "@components/input/strings/StringBox";
import DandDButtonBox from "@dndComponents/DandDButtonBox";

const darks = ["red","green","blue","purple","black","gray"];

interface Props { 
  obj: EncounterParticipant;
  encounter: Encounter;
  selected_participant_id: string;
  selected_ci_id: string;
  current_turn_participant_id: string;
  disabled?: boolean;
  change_time: number;
  onSelect: (ci: BasicCreatureInstance | CustomParticipant | null) => void;
  onChange: () => void;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function InitiativeTrackerParticipant({
  obj,
  encounter,
  selected_participant_id,
  selected_ci_id,
  current_turn_participant_id,
  disabled = false,
  change_time,
  onSelect,
  onChange,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [prev_change_time, setChangeTime] = useState(change_time);
  const [menu_open, setMenuOpen] = useState("");
  const [using_ci, setUsingCI] = useState<BasicCreatureInstance | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [manager_open, setManagerOpen] = useState(false);
  const [ping, setPing] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = () => {
    setReloading(true);
  }

  const resourcesChanged = () => {
    onChange();
    setReloading(true);
  }

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      obj.subscription_holder.subscribe("Recalculated", mounted, recalculated);
      obj.subscription_holder.subscribe("Resources", mounted, resourcesChanged);
    }
  });

  // eslint-disable-next-line
  useEffect(() => {
    if (change_time !== prev_change_time) {
      setLoading(false);
      setMenuOpen("");
      setChangeTime(change_time);
    }
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const renderCRSelect = () => {
    if (!disabled) {
      if (creature_instance instanceof BasicCreatureInstance && 
        creature_instance.creature && 
        (creature_instance.creature.min_challenge_rating !== creature_instance.creature.challenge_rating || 
          creature_instance.creature.max_challenge_rating !== creature_instance.creature.challenge_rating)) {
        const options: DisablableOption[] = [];
        if (creature_instance.creature.name_table.length > 0) {
          for (let i = creature_instance.creature.min_challenge_rating; i <= creature_instance.creature.max_challenge_rating; ++i) {
            const name_for_cr = creature_instance.creature.name_table.find(o => o.level === i);
            if (name_for_cr) {
              options.push({ id: name_for_cr.name, text: name_for_cr.name, disabled: true });
            }
            options.push({ id: `${i}`, text: `${i}` });
          }
        } else {
          for (let i = creature_instance.creature.min_challenge_rating; i <= creature_instance.creature.max_challenge_rating; ++i) {
            options.push({ id: `${i}`, text: `${i}` });
          }
        }
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox
              name="Challenge Rating"
              options={options}
              value={`${creature_instance.challenge_rating}`}
              onChange={async (changed: string) => {
                const new_obj = creature_instance;
                await new_obj.change_challenge_rating(+changed);
                onChange();
                setReloading(true);
              }}
            />
          </Grid>
        );
      } else if (obj.participant instanceof CustomParticipant) {
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox
              name="Challenge Rating"
              options={CR_OPTIONS}
              value={`${obj.participant.challenge_rating}`}
              onChange={async (changed: string) => {
                const new_obj = obj;
                if (new_obj.participant instanceof CustomParticipant) {
                  new_obj.participant.challenge_rating = +changed;
                  for (const cp of new_obj.child_participants) {
                    if (cp instanceof CustomParticipant) {
                      cp.challenge_rating = +changed;
                    }
                  }
                }
                onChange();
                setReloading(true);
              }}
            />
          </Grid>
        );
      }
    }
    return null;
  }

  const renderManage = () => {
    const participant = obj.participant;
    if (participant instanceof BasicCreatureInstance) {
      const others = encounter.participants.filter(o => 
        o.creature_id === obj.creature_id && 
        o.participant_id !== obj.participant_id);
      const creature = participant.creature;
      return (
        <ModalOrDrawer
          open={manager_open}
          onClose={() => {
            setManagerOpen(false);
            obj.subscription_holder.modalClosed();
          }}
        >
          <>
          { manager_open &&
            <Grid container spacing={0} direction="row">
              <Grid size={12} className="header centered">Manage</Grid>
              { renderCRSelect() }
              <Grid size={12}>
                <Tooltip title={`Add Another`}>
                  <span>
                    <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                      disabled={ disabled }
                      onClick={ async () => {
                        const creature2 = creature;
                        if (creature2) {
                          const new_obj = obj;
                          const new_creature_inst = new BasicCreatureInstance();
                          await new_creature_inst.copyCreature(creature2, creature_instance ? creature_instance.challenge_rating : -1);
                          // if (creature_instance && creature_instance.creature_gifts.length > 0) {
                          //   for (const cg of creature_instance.creature_gifts) {
                          //     if (cg.gift) {
                          //       new_creature_inst.copyCreatureGift(cg.gift, cg.level);
                          //     }
                          //   }
                          //   await new_creature_inst.connectCreature(creature2);
                          // }
                          new_creature_inst._num = obj.child_participants.length + 1;
                          new_obj.child_participants.push(new_creature_inst);
                          onChange();
                          setReloading(true);
                        }
                      }}>
                      <Add/>
                    </Fab>
                  </span>
                </Tooltip>
              </Grid>
              <Grid
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                { !reloading && obj.child_participants.map((child, key) => {
                  if (child instanceof BasicCreatureInstance) {
                    return (
                      <Grid
                        key={child.true_id}
                        container
                        spacing={0}
                        direction="row"
                        className={ key % 2 === 0 ? "alternate" : "" }
                        size={{
                          xs: 12
                        }}>
                        <Grid
                          size={{
                            xs: 4
                          }}>{ child.name } (HP: {child.hp.current})</Grid>
                        <Grid
                          size={{
                            xs: 4
                          }}>
                          <ButtonBox
                            name="Split"
                            disabled={ disabled || obj.child_participants.length === 1 }
                            onClick={() => {
                              // debugger;
                              const new_obj = obj;
                              const new_ci = new EncounterParticipant();
                              new_ci.creature_id = new_obj.creature_id;
                              new_ci.participant_id = child.true_id;
                              new_ci.owner_id = new_obj.owner_id;
                              new_ci.color = new_obj.color;
                              new_ci.participant_type = new_obj.participant_type;
                              new_ci.participant = child;
                              new_ci.child_participants = [child];
                              encounter.participants.push(new_ci);
                              new_obj.child_participants = new_obj.child_participants.filter(o => o instanceof BasicCreatureInstance && o.true_id !== child.true_id);
                              
                              if (new_obj.participant instanceof BasicCreatureInstance && new_obj.participant.true_id === child.true_id) {
                                new_obj.participant = new_obj.child_participants[0];
                              }
                              onChange();
                              setReloading(true);
                            }}
                          />
                        </Grid>
                        <Grid
                          size={{
                            xs: 4
                          }}>
                          <ClearButton
                            disabled={ disabled }
                            onClick={ () => {
                              if (obj.child_participants.length === 1) {
                                encounter.participants = encounter.participants.filter(o => o.participant_id !== obj.participant_id);
                                
                                onChange();
                                setManagerOpen(false);
                              } else {
                                const new_obj = obj;
                                new_obj.child_participants = new_obj.child_participants.filter(o => o instanceof BasicCreatureInstance && o.true_id !== child.true_id);
                                
                                if (new_obj.participant instanceof BasicCreatureInstance && new_obj.participant.true_id === child.true_id) {
                                  new_obj.participant = new_obj.child_participants[0];
                                }
                                onChange();
                                setReloading(true);
                              }
                            }} 
                          />
                        </Grid>
                      </Grid>
                    );
                  }
                  return null;
                })}
              </Grid>
              { others.length > 0 &&
                <>
                  <Grid
                    className="header centered"
                    size={{
                      xs: 12
                    }}>Join</Grid>
                  <Grid
                    container
                    spacing={0}
                    direction="row"
                    size={{
                      xs: 12
                    }}>
                    { !reloading && others.map((other, key) => {
                      return (
                        <Grid
                          key={key}
                          className="clickable"
                          onClick={() => {
                            const new_obj = obj;
                            new_obj.child_participants = [...new_obj.child_participants,...other.child_participants];
                            other.child_participants = [];
                            encounter.participants = encounter.participants.filter(o => o.participant_id !== other.participant_id);
                            
                            onChange();
                            setReloading(true);
                          }}
                          size={{
                            xs: 12
                          }}>
                          { other.name }
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              }
            </Grid>
          }
          </>
        </ModalOrDrawer>
      );
    } else if (participant instanceof CustomParticipant) {
      // debugger;
      return (
        <ModalOrDrawer
          open={manager_open}
          onClose={() => {
            setManagerOpen(false);
            obj.subscription_holder.modalClosed();
          }}
        >
          <Grid container spacing={0} direction="row">
            <Grid
              className="header centered"
              size={{
                xs: 12
              }}>Manage</Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox
                name="Name"
                value={participant.name}
                disabled={disabled}
                onBlur={(changed: string) => {
                  participant.name = changed;
                  for (const cp of obj.child_participants) {
                    if (cp instanceof CustomParticipant) {
                      cp.name = changed;
                    }
                  }
                  onChange();
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <StringBox
                name="Max Hit Points"
                value={`${participant.hp.max}`}
                type="number"
                disabled={disabled}
                onBlur={(changed: string) => {
                  const new_hp = +changed;
                  const old_hp = participant.hp.max;
                  participant.hp.max = new_hp;
                  if (participant.hp.current === old_hp) {
                    participant.hp.current = new_hp;
                  }
                  participant.subscription_holder.resourcesChanged();
                  for (const cp of obj.child_participants) {
                    if (cp instanceof CustomParticipant) {
                      cp.hp.max = new_hp;
                      if (cp.hp.current === old_hp) {
                        cp.hp.current = new_hp;
                      }
                      cp.subscription_holder.resourcesChanged();
                    }
                  }
                  onChange();
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <StringBox
                name="Armor Class"
                value={`${participant.armor_class}`}
                type="number"
                disabled={disabled}
                onBlur={(changed: string) => {
                  participant.armor_class = +changed;
                  for (const cp of obj.child_participants) {
                    if (cp instanceof CustomParticipant) {
                      cp.armor_class = +changed;
                    }
                  }
                  onChange();
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <SelectStringBox
                name="Challenge Rating"
                value={`${participant.challenge_rating}`}
                options={CR_OPTIONS}
                disabled={disabled}
                onChange={(changed: string) => {
                  participant.challenge_rating = +changed;
                  for (const cp of obj.child_participants) {
                    if (cp instanceof CustomParticipant) {
                      cp.challenge_rating = +changed;
                    }
                  }
                  onChange();
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <StringBox
                name="Initiative Modifier"
                value={`${participant.initiative_modifier}`}
                type="number"
                disabled={disabled}
                onBlur={(changed: string) => {
                  participant.initiative_modifier = +changed;
                  onChange();
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <Tooltip title={`Add Another`}>
                <span>
                  <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                    disabled={ disabled }
                    onClick={ async () => {
                      // debugger;
                      const new_obj = obj;
                      const new_cp = participant.clone();
                      new_cp.true_id = uuidv4().toString();
                      new_obj.child_participants.push(new_cp);
                      onChange();
                      setPing(!ping);
                    }}>
                    <Add/>
                  </Fab>
                </span>
              </Tooltip>
            </Grid>
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              { !reloading && obj.child_participants.map((child, key) => {
                if (child instanceof CustomParticipant) {
                  return (
                    <Grid
                      key={child.true_id}
                      container
                      spacing={0}
                      direction="row"
                      className={ key % 2 === 0 ? "alternate" : "" }
                      size={{
                        xs: 12
                      }}>
                      <Grid
                        size={{
                          xs: 4
                        }}>{ child.name } (HP: {child.hp.current})</Grid>
                      <Grid
                        size={{
                          xs: 4
                        }}>
                        <ButtonBox
                          name="Split"
                          disabled={ disabled || obj.child_participants.length === 1 }
                          onClick={() => {
                            const new_obj = obj;
                            const new_ci = new EncounterParticipant();
                            new_ci.participant_id = child.true_id;
                            new_ci.creature_id = new_obj.creature_id;
                            new_ci.participant_id = child.true_id;
                            new_ci.owner_id = new_obj.owner_id;
                            new_ci.color = new_obj.color;
                            new_ci.participant_type = new_obj.participant_type;
                            new_ci.participant = child;
                            new_ci.child_participants = [child];
                            encounter.participants.push(new_ci);
                            new_obj.child_participants = new_obj.child_participants.filter(o => o instanceof BasicCreatureInstance && o.true_id !== child.true_id);
                            
                            if (new_obj.participant instanceof BasicCreatureInstance && new_obj.participant.true_id === child.true_id) {
                              new_obj.participant = new_obj.child_participants[0];
                            }
                            onChange();
                            setPing(!ping);
                          }}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 4
                        }}>
                        <ClearButton
                          disabled={ disabled }
                          onClick={ () => {
                            if (obj.child_participants.length === 1) {
                              encounter.participants = encounter.participants.filter(o => o.participant_id !== obj.participant_id);
                              
                              onChange();
                              setManagerOpen(false);
                            } else {
                              const new_obj = obj;
                              new_obj.child_participants = new_obj.child_participants.filter(o => o instanceof BasicCreatureInstance && o.true_id !== child.true_id);
                              
                              if (new_obj.participant instanceof BasicCreatureInstance && new_obj.participant.true_id === child.true_id) {
                                new_obj.participant = new_obj.child_participants[0];
                              }
                              onChange();
                              setPing(!ping);
                            }
                          }} 
                        />
                      </Grid>
                    </Grid>
                  );
                }
                return null;
              })}
            </Grid>
          </Grid>
        </ModalOrDrawer>
      );
    }
    return null;
  }

  const renderInfo = () => {
    let return_me: any = null;
    if (!(obj.participant instanceof Character)) {
      return_me = (
        <>
          <DandDButtonBox
            name="Manage"
            disabled={disabled}
            color={ obj.color && !darks.includes(obj.color) ? "black" : (obj.color ? "white" : undefined)}
            onClick={() => {
              // debugger;
              setManagerOpen(true);
            }}
          />
          { manager_open && renderManage() }
        </>
      );
    }
    return (
      <>
        { obj.participant instanceof BasicCreatureInstance &&
          <Grid
            size={{
              xs: 12
            }}>
            <CreatureInstanceSavingThrows 
              obj={obj.participant}
              disabled={ disabled }
              trim
              color={obj.color}
              onChange={() => {

              }}
            />
          </Grid>
        }
        <Grid
          size={{
            xs: 6
          }}>{ return_me }</Grid>
        <Grid
          size={{
            xs: 2
          }}>
          { api.user && encounter.owner_id === api.user._id &&
            <ColorButtonBox 
              color={obj.color}
              onChange={(changed: string) => {
                const new_obj = obj
                new_obj.color = changed;
                setPing(!ping);
              }}
            />
          }
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          { api.user && encounter.owner_id === api.user._id &&
            <ClearButton
              disabled={ disabled || obj.participant_id === current_turn_participant_id }
              onClick={ () => {
                encounter.participants = encounter.participants.filter(o => o.participant_id !== obj.participant_id);
                
                onChange();
              }} 
            />
          }
        </Grid>
      </>
    );
  }

  const renderSlots = (creature_instance: BasicCreatureInstance | null) => {
    if (creature_instance) {
      return creature_instance.use_slots2.map((r, key) => {
        const level = +r.resource_id.substring(6);
        const slot_count = r.total;
        if (slot_count && slot_count > 0) {
          let level_name = "";
          switch(+level) {
            case 1: level_name = "1st"; break;
            case 2: level_name = "2nd"; break;
            case 3: level_name = "3rd"; break;
            default: level_name = `${level}th`; break;
          }
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              <CharacterResourceBoxesNew 
                used={creature_instance.used_slots[+level]}
                total={slot_count}
                true_total={slot_count}
                created={0}
                type={`${level_name} Level Slot${ slot_count > 0 && "s" }`}
                shared={false}
                onChange={(used: number) => {
                  if (!disabled) {
                    const new_obj = obj;
                    const ci = new_obj.participant as BasicCreatureInstance;

                    ci.used_slots[+level] = used;
                    ci.subscription_holder.resourcesChanged();
                    setPing(!ping);
                  }
                }}
              />
            </Grid>
          );
        } else {
          return null;
        }
      });
    }
    return null;
  }

  const renderConditions = (creature_instance: BasicCreatureInstance | null = null) => {
    const part = creature_instance ? creature_instance : obj.participant;
    if (part && !(part instanceof Character)) {
      const active_conditions = Object.keys(part.condition_hash);
      return (
        <Grid
          style={{ cursor: "pointer" }}
          onClick={() => {
            setMenuOpen("conditions");
            setUsingCI(creature_instance);
          }}
          container
          spacing={0}
          direction="column"
          size={{
            xs: 12
          }}>
          <Grid>
            { active_conditions.length === 0 ?
              "No Conditions" 
            :
              <DisplayObjects type="condition" ids={active_conditions} />
            }
          </Grid>
          { part.condition_immunities.length > 0 &&
            <Grid>
              Immunities: <DisplayObjects type="condition" ids={part.condition_immunities} />
            </Grid>
          }
        </Grid>
      );
    }
    return null;
  }

  const renderExtras = () => {
    return (
      <ModalOrDrawer 
        open={ menu_open === "conditions" } 
        onClose={() => {
          setMenuOpen("");
          obj.subscription_holder.modalClosed();
        }}>
        { renderAllConditions() }
      </ModalOrDrawer>
    );
  }

  const renderAllConditions = () => {
    const part = using_ci ? using_ci : obj.participant;
    if (part) {
      return (
        <ConditionSideBar 
          menu_open={ menu_open === "conditions" }
          active_conditions={part.condition_hash}
          starred={part.favorite_conditions}
          immune={part.condition_immunities}
          extra_ids={["Any"]}
          disabled={disabled}
          color="blue"
          onChange={(active_conditions: { [id: string]: { level: number, source_true_id: string } }, starred: string[], immune: string[]) => {
            const new_obj = obj;
            const part2 = using_ci ? using_ci : new_obj.participant;
            if (part2) {
              // part2.condition_hash = active_conditions;
              part2.favorite_conditions = starred;
              part2.condition_immunities = immune;
              setMenuOpen("");
              onChange();
            }
          }}
        />
      );
    }
    return null;
  }

  const participantName = () => {
    if (obj.participant_type === "Lair Actions") {
      return "Lair Actions";
    } else {
      return obj.name;
    }
  }

  const api_user_id = api.user ? api.user._id : "";
  const my_turn = current_turn_participant_id === obj.participant_id;
  const selected = selected_participant_id === obj.participant_id;
  const creature_instance: BasicCreatureInstance | null = obj.participant && obj.participant instanceof BasicCreatureInstance ? obj.participant : null;
  const creature: Creature | undefined = creature_instance ? creature_instance.creature : undefined;
  const bg_class_name = `bg_${obj.color}${obj.include_in_difficulty ? "" : "_striped"}`;
  
  const renderCreatures = () => {
    const shield = darks.includes(obj.color) ? ShieldWhite : ShieldBlack;
    return (
      <>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 9
          }}>
          <Grid
            className={`${disabled ? "" : "clickable"}`}
            onClick={() => {
                if (!disabled) {
                  onSelect(null);
                }
              }}
            size={{
              xs: 4
            }}>
            { name }
          </Grid>
          <Grid
            style={{ position: "relative" }}
            size={{
              xs: 6
            }}>
            { !disabled &&
              <>
                <img src={ darks.includes(obj.color) ? ShieldWhite : ShieldBlack } alt="shield" 
                  style={{
                    position: "absolute",
                    width: "30px",
                    height: "30px",
                    zIndex: 2
                  }}
                />
                <div className="centered" style={{
                  width: "30px",
                  height: "30px"
                }}>
                  <span style={{ lineHeight: "1.7" }}>
                    { obj.ac }
                  </span>
                </div>
              </>
            }
          </Grid>
          <Grid
            size={{
              xs: 2
            }}>
            <ExpandButton 
              is_expanded={expanded}
              onClick={() =>{
                setExpanded(!expanded);
              }}
            />
          </Grid>
          { creature ?
            <Grid
              className="small_label gray"
              size={{
                xs: 12
              }}>
              {creature.creature_type}, CR: {creature.challenge_rating}
            </Grid>
          : obj.participant instanceof CustomParticipant &&
            <Grid
              className="small_label gray"
              size={{
                xs: 12
              }}>
              CR: { obj.participant.challenge_rating }
            </Grid>
          }
        </Grid>
        { expanded &&
          <>
            { renderInfo() }
            <Grid
              size={{
                xs: 12
              }}>
              { api.user && encounter.owner_id === api.user._id &&
                <ToggleButtonBox 
                  name="Include in Difficulty"
                  value={ obj.include_in_difficulty }
                  disabled={ disabled }
                  color={ obj.color && !darks.includes(obj.color) ? "black" : undefined}
                  onToggle={ () => {
                    const new_obj = obj;
                    if (new_obj) {
                      new_obj.include_in_difficulty = !new_obj.include_in_difficulty;
                    }
                    onChange();
                  }} 
                />
              }
            </Grid>
            { obj.child_participants.map(ci => {
              if (ci instanceof BasicCreatureInstance || ci instanceof CustomParticipant) {
                const ci_selected = selected_ci_id === ci.true_id;
                return (
                  <Grid
                    key={ci.true_id}
                    container
                    spacing={0}
                    direction="row"
                    className={bg_class_name}
                    style={{ 
                      border: (my_turn ? "4px solid #138f13" : (ci_selected ? "2px solid blue" : "2px solid black")),
                      color: darks.includes(obj.color) ? "white" : "black",
                      padding: "4px"
                    }}
                    size={{
                      xs: 12
                    }}>
                    <Grid
                      className={`${disabled ? "" : "clickable"}`}
                      onClick={() => {
                          if (!disabled) {
                            onSelect(ci);
                          }
                        }}
                      size={{
                        xs: 6
                      }}>
                      { ci.name }
                    </Grid>
                    <Grid
                      style={{ position: "relative" }}
                      size={{
                        xs: 3
                      }}>
                      <img src={ shield } alt="shield" 
                        style={{
                          position: "absolute",
                          width: "30px",
                          height: "30px",
                          zIndex: 2
                        }}
                      />
                      <div className="centered" style={{
                        width: "30px",
                        height: "30px"
                      }}>
                        <span style={{ lineHeight: "1.7" }}>
                          { ci.armor_class }
                        </span>
                      </div>
                    </Grid>
                    <Grid
                      size={{
                        xs: 3
                      }}>
                      <HPInput
                        char={ci}
                        disabled={ disabled }
                        borderColor={ darks.includes(obj.color) ? "white" : "black" }
                      />
                    </Grid>
                    { ci instanceof BasicCreatureInstance &&
                      <>
                        <Grid
                          container
                          spacing={0}
                          direction="row"
                          size={{
                            xs: 12
                          }}>
                          { renderSlots(ci) }
                          { renderConditions(ci) }
                        </Grid>
                        { ci.legendary_actions_per_round > 0 && (obj.owner_id === api_user_id || encounter.owner_id === api_user_id) &&
                          <Grid
                            size={{
                              xs: 12
                            }}>
                            <CharacterResourceBoxesNew 
                              used={ci.used_legendary_actions}
                              total={ci.legendary_actions_per_round}
                              true_total={ci.legendary_actions_per_round}
                              created={0}
                              type={"Legendary Actions"}
                              shared={false}
                              onChange={(used: number) => {
                                if (!disabled) {
                                  ci.used_legendary_actions = used;
                                  ci.subscription_holder.resourcesChanged();
                                  setPing(!ping);
                                }
                              }}
                            />
                          </Grid>
                        }
                        { ci.legendary_resistances > 0 && (obj.owner_id === api_user_id || encounter.owner_id === api_user_id) &&
                          <Grid
                            size={{
                              xs: 12
                            }}>
                            <CharacterResourceBoxesNew 
                              used={ci.used_legendary_resistances}
                              total={ci.legendary_resistances}
                              true_total={ci.legendary_resistances}
                              created={0}
                              type={"Legendary Resistances"}
                              shared={false}
                              onChange={(used: number) => {
                                if (!disabled) {
                                  ci.used_legendary_resistances = used;
                                  ci.subscription_holder.resourcesChanged();
                                  setPing(!ping);
                                }
                              }}
                            />
                          </Grid>
                        }
                      </>
                    }
                  </Grid>
                );
              }
              return null;
            })}
          </>
        }
      </>
    );
  }

  const renderContent = () => {
    // if (obj.participant_id === "Lair Actions") {
    //   debugger;
    // }
    if (name === "Loading" || loading) {
      return (
        <Grid
          size={{
            xs: 9
          }}><LoadingBox /></Grid>
      );
    } else if (obj.participant && obj.child_participants.length < 2) {
      return (
        <>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 4
            }}>
            <Grid
              className={disabled ? "" : `clickable`}
              onClick={() => {
                if (!disabled) {
                  onSelect(null);
                }
              }}
              size={{
                xs: 9
              }}>{ name }</Grid>
            <Grid
              style={{ position: "relative" }}
              size={{
                xs: 3
              }}>
              { !disabled &&
                <>
                  <img src={ darks.includes(obj.color) ? ShieldWhite : ShieldBlack } alt="shield" 
                    style={{
                      position: "absolute",
                      width: "30px",
                      height: "30px",
                      zIndex: 2
                    }}
                  />
                  <div className="centered" style={{
                    width: "30px",
                    height: "30px"
                  }}>
                    <span style={{ lineHeight: "1.7" }}>
                      { obj.ac }
                    </span>
                  </div>
                </>
              }
            </Grid>
            { obj.participant instanceof Character ?
              <Grid
                className="small_label gray"
                size={{
                  xs: 12
                }}>
                { obj.participant.race_and_class_string.replace("(","").replace(")","") }
              </Grid>
            : obj.participant instanceof BasicCreatureInstance ?
              <Grid
                className="small_label gray"
                size={{
                  xs: 12
                }}>
                {obj.participant.creature_type}, CR: {obj.participant.challenge_rating}
              </Grid>
            : obj.participant instanceof CustomParticipant &&
              <Grid
                className="small_label gray"
                size={{
                  xs: 12
                }}>
                CR: { obj.participant.challenge_rating }
              </Grid>
            }
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            { !(obj.participant instanceof Character) &&
              <HPInput
                char={obj.participant}
                disabled={ disabled }
                borderColor={ darks.includes(obj.color) ? "white" : "black" }
              />
            }
          </Grid>
          <Grid
            size={{
              xs: 2
            }}>
            <ExpandButton 
              is_expanded={expanded}
              onClick={() =>{
                setExpanded(!expanded);
              }}
            />
          </Grid>
          { expanded &&
            <>
              { renderInfo() }
              <Grid
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 6
                }}>
                { renderSlots(creature_instance) }
                { renderConditions() }
              </Grid>
              <Grid
                size={{
                  xs: 6
                }}>
                { api.user && encounter.owner_id === api.user._id &&
                  <ToggleButtonBox 
                    name="Include in Difficulty"
                    value={ obj.include_in_difficulty }
                    disabled={ disabled }
                    color={ obj.color && !darks.includes(obj.color) ? "black" : undefined}
                    onToggle={ () => {
                      const new_obj = obj;
                      if (new_obj) {
                        new_obj.include_in_difficulty = !new_obj.include_in_difficulty;
                      }
                      onChange();
                    }} 
                  />
                }
              </Grid>
              { creature_instance && creature && creature_instance.legendary_actions_per_round > 0 && (obj.owner_id === api_user_id || encounter.owner_id === api_user_id) &&
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <CharacterResourceBoxesNew 
                    used={creature_instance.used_legendary_actions}
                    total={creature_instance.legendary_actions_per_round}
                    true_total={creature_instance.legendary_actions_per_round}
                    created={0}
                    type={"Legendary Actions"}
                    // show_type={false}
                    shared={false}
                    onChange={(used: number) => {
                      if (!disabled) {
                        const new_obj = obj;
                        const part = new_obj.participant;
                                      
                        if (part instanceof BasicCreatureInstance) {
                          part.used_legendary_actions = used;
                          new_obj.subscription_holder.resourcesChanged();
                          setPing(!ping);
                        }
                      }
                    }}
                  />
                </Grid>
              }
              { creature_instance && creature && creature_instance.legendary_resistances > 0 && (obj.owner_id === api_user_id || encounter.owner_id === api_user_id) &&
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <CharacterResourceBoxesNew 
                    used={creature_instance.used_legendary_resistances}
                    total={creature_instance.legendary_resistances}
                    true_total={creature_instance.legendary_resistances}
                    created={0}
                    type={"Legendary Resistances"}
                    // show_type={false}
                    shared={false}
                    onChange={(used: number) => {
                      if (!disabled) {
                        const new_obj = obj;
                        const part = new_obj.participant;
                                      
                        if (part instanceof BasicCreatureInstance) {
                          part.used_legendary_resistances = used;
                          new_obj.subscription_holder.resourcesChanged();
                          setPing(!ping);
                        }
                      }
                    }}
                  />
                </Grid>
              }
            </>
          }
        </>
      );
    } else if (obj.participant instanceof BasicCreatureInstance || obj.participant instanceof CustomParticipant) {
      return renderCreatures();
    } else if (obj.participant_type === "Lair Actions") {
      return (
        <>
          <Grid
            size={{
              xs: 7
            }}>Lair Actions</Grid>
          <Grid
            size={{
              xs: 2
            }}>
            <ClearButton
              onClick={() =>{
                encounter.participants = encounter.participants.filter(o => o.participant_id !== obj.participant_id);
                
                onChange();
              }}
            />
          </Grid>
        </>
      );
    }
  }

  const name = participantName();
  return (
    <Grid container spacing={0} direction="row" className={bg_class_name}
      style={{ 
        border: (my_turn ? "4px solid #138f13" : (selected ? "2px solid blue" : "2px solid black")),
        // backgroundColor: obj.color,
        color: darks.includes(obj.color) ? "white" : "black",
        padding: "4px"
      }}>
      { my_turn &&
        <Grid
          size={{
            xs: 1
          }}>
          &rarr;
        </Grid>
      }
      <Grid
        size={{
          xs: my_turn ? 1 : 2
        }}>
        <EditableLabel 
          name="Init"
          value={ obj.initiative === null ? "" : `${obj.initiative}` }
          display_name
          fontSize={16}
          color={darks.includes(obj.color) ? "white" : "black"}
          disabled={disabled || encounter.running}
          onChange={(changed: string) => {
            const new_obj = obj;
            if (new_obj) {
              if (changed === "") {
                new_obj.initiative = null;
              } else {
                new_obj.initiative = common_util.clean_number_string(changed);
              }
            }
            onChange();
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 1
        }}></Grid>
      { renderContent() }
      { renderExtras() }
    </Grid>
  );
}
